import React from 'react';
import PropTypes from 'prop-types';
import parser from 'query-string-parser';

import Utils from '../../utils/utils';

export default class StaticMap extends React.PureComponent {
    static propTypes = {
        center: PropTypes.shape({
            lat: PropTypes.number.isRequired,
            lng: PropTypes.number.isRequired
        }),
        radius: PropTypes.number,
        bounds: PropTypes.shape({
            sw: PropTypes.shape({ lat: PropTypes.number.isRequired, lon: PropTypes.number.isRequired })
                .isRequired,
            ne: PropTypes.shape({ lat: PropTypes.number.isRequired, lon: PropTypes.number.isRequired })
                .isRequired
        }),
        /** Logical size of the image in bounds mode; match the rendered box so the zoom reads 1:1. */
        width: PropTypes.number,
        height: PropTypes.number,
        /** Zoom to render in bounds mode; defaults to the largest zoom that shows the whole zone. */
        boundsZoom: PropTypes.number,
        onClick: PropTypes.func,
        type: PropTypes.oneOf(['google', 'here']),
        zoom: PropTypes.number
    };

    static defaultProps = {
        type: 'google',
        zoom: 14
    };

    static markerIcon = `${Utils.getAssetsDomain()}/images/website/search/marker-default-main-v2.svg`;

    static IMAGE_WIDTH = 800;
    static IMAGE_HEIGHT = 450;
    static MAX_IMAGE_SIZE = 640;
    static MAX_ZOOM = 20;
    static TILE_SIZE = 256;

    circleRadiuses = {};

    constructor(props) {
        super(props);
        this.container = React.createRef();
    }

    computeRadius(zoom) {
        const { radius: minRadius } = this.props;
        let radius = 150;

        if (zoom < 14) {
            if (!this.circleRadiuses[zoom]) {
                this.circleRadiuses[zoom] = Math.pow(2, 21 - zoom) * 1128.49722 * 0.001;
            }
            radius = this.circleRadiuses[zoom];
        }

        if (minRadius) {
            return Math.max(radius, minRadius);
        }

        return radius;
    }

    /**
     * Calculates the end-point from a given source at a given range (meters) and bearing (degrees).
     * This methods uses simple geometry equations to calculate the end-point.
     * @see https://en.wikipedia.org/wiki/GNSS_positioning_calculation
     * @see https://stackoverflow.com/questions/1125144/how-do-i-find-the-lat-long-that-is-x-km-north-of-a-given-lat-long
     *
     * @param {LatLng} origin - Point of origin
     * @param {double} range - Range in meters
     * @param {double} bearing - Bearing in degrees
     * @return {LatLng} End-point from the source given the desired range and bearing.
     */
    calculateDerivedPosition(origin, range, bearing) {
        const earthRadius = 6378137.0;
        const degreesToRadians = Math.PI / 180.0;
        const radiansToDegrees = 180.0 / Math.PI;

        const latA = origin.lat * degreesToRadians;
        const lonA = origin.lng * degreesToRadians;
        const angularDistance = range / earthRadius;
        const trueCourse = bearing * degreesToRadians;

        const lat = Math.asin(
            Math.sin(latA) * Math.cos(angularDistance) +
                Math.cos(latA) * Math.sin(angularDistance) * Math.cos(trueCourse)
        );

        const dlon = Math.atan2(
            Math.sin(trueCourse) * Math.sin(angularDistance) * Math.cos(latA),
            Math.cos(angularDistance) - Math.sin(latA) * Math.sin(lat)
        );

        const lon = ((lonA + dlon + Math.PI) % (Math.PI * 2)) - Math.PI;

        return {
            lat: lat * radiansToDegrees,
            lon: lon * radiansToDegrees
        };
    }

    getCiclePoints(center, zoom, nbPoints) {
        const range = this.computeRadius(zoom);
        const points = [];
        for (let point = 1; point <= nbPoints + 1; point++) {
            const bearing = (360 / nbPoints) * point;
            const { lat, lon } = this.calculateDerivedPosition(center, range, bearing);
            points.push(`${lat}, ${lon}`);
        }
        return points;
    }

    static project(lat, lon, zoom) {
        const scale = StaticMap.TILE_SIZE * 2 ** zoom;
        const sin = Math.sin((lat * Math.PI) / 180);
        return {
            x: ((lon + 180) / 360) * scale,
            y: (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * scale
        };
    }

    static unproject(x, y, zoom) {
        const scale = StaticMap.TILE_SIZE * 2 ** zoom;
        const n = Math.PI - (2 * Math.PI * y) / scale;
        return {
            lat: (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n))),
            lon: (x / scale) * 360 - 180
        };
    }

    /** Fractional zoom at which the zone exactly fills a `width` x `height` viewport. */
    static fitZoom(bounds, width, height) {
        const sw = StaticMap.project(bounds.sw.lat, bounds.sw.lon, 0);
        const ne = StaticMap.project(bounds.ne.lat, bounds.ne.lon, 0);
        const spanX = Math.max(ne.x - sw.x, Number.EPSILON);
        const spanY = Math.max(sw.y - ne.y, Number.EPSILON);
        return Math.log2(Math.min(width / spanX, height / spanY));
    }

    static boundsCenter(bounds) {
        const sw = StaticMap.project(bounds.sw.lat, bounds.sw.lon, 0);
        const ne = StaticMap.project(bounds.ne.lat, bounds.ne.lon, 0);
        return StaticMap.unproject((sw.x + ne.x) / 2, (sw.y + ne.y) / 2, 0);
    }

    /**
     * Static Maps only takes whole zoom levels and images up to 640px: a fractional zoom is rendered at
     * the next level on a larger image, or the level below on a smaller one when 640px would not do,
     * and the box scales it back to the exact same scale.
     */
    generateGoogleBoundsStaticMap() {
        const { bounds, boundsZoom } = this.props;
        const width = this.props.width || StaticMap.IMAGE_WIDTH;
        const height = this.props.height || StaticMap.IMAGE_HEIGHT;
        const targetZoom = Number.isFinite(boundsZoom)
            ? boundsZoom
            : StaticMap.fitZoom(bounds, width, height);
        const fitsAbove =
            Math.max(width, height) * 2 ** (Math.ceil(targetZoom) - targetZoom) <= StaticMap.MAX_IMAGE_SIZE;
        const level = fitsAbove ? Math.ceil(targetZoom) : Math.floor(targetZoom);
        const zoom = Math.max(0, Math.min(StaticMap.MAX_ZOOM, level));
        const upscale = 2 ** (zoom - targetZoom);
        const center = StaticMap.boundsCenter(bounds);
        const params = [
            ['key', process.env.GOOGLE_API_KEY],
            ['size', `${Math.round(width * upscale)}x${Math.round(height * upscale)}`],
            ['scale', '2'],
            ['center', `${center.lat.toFixed(6)},${center.lon.toFixed(6)}`],
            ['zoom', String(zoom)]
        ];
        if (process.env.GOOGLE_MAPS_MAP_ID) {
            params.push(['map_id', process.env.GOOGLE_MAPS_MAP_ID]);
        }
        const query = params.map(([key, value]) => `${key}=${encodeURIComponent(value)}`).join('&');

        return `https://maps.googleapis.com/maps/api/staticmap?${query}`;
    }

    generateGoogleStaticMap() {
        const { center, radius, zoom, bounds } = this.props;

        if (bounds) {
            return this.generateGoogleBoundsStaticMap();
        }

        const options = {
            key: process.env.GOOGLE_API_KEY,
            size: '800x450',
            scale: 2,
            zoom
        };

        if (radius) {
            const points = this.getCiclePoints(center, zoom, 45); // 360 / 8 = 45
            options.path = `color:0x${Utils.PRIMARY_COLOR}|weight:2|fillcolor:0x${
                Utils.PRIMARY_COLOR
            }33|${points.join('|')}`;
        } else {
            options.center = `${center.lat},${center.lng}`;
            options.markers = `anchor:bottom|icon:${StaticMap.markerIcon}|${center.lat},${center.lng}`;
        }

        return `https://maps.googleapis.com/maps/api/staticmap?${parser.toQuery(options)}`;
    }

    generateHereImageMap() {
        const { center, radius, zoom } = this.props;

        const options = {
            w: 800,
            h: 450,
            lat: center.lat,
            lon: center.lng,
            nodot: true,
            z: zoom,
            app_id: process.env.HERE_APP_ID,
            app_code: process.env.HERE_APP_CODE
        };

        if (radius) {
            options.u = radius;
        } else {
            options.poix0 = `${center.lat},${center.lng};${Utils.PRIMARY_COLOR};ffffff;34;⚫`;
        }

        return `https://image.maps.api.here.com/mia/1.6/mapview?${parser.toQuery(options)}`;
    }

    generateImageUrl(type) {
        switch (type) {
            case 'google':
                return this.generateGoogleStaticMap();
            case 'here':
                return this.generateHereImageMap();
            default:
                return null;
        }
    }

    render() {
        const { onClick, type } = this.props;
        const imageUrl = this.generateImageUrl(type);
        return (
            <div
                style={{
                    backgroundImage: `url(${imageUrl})`,
                    backgroundSize: 'cover',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    height: '100%',
                    cursor: onClick ? 'pointer' : 'auto'
                }}
                onClick={onClick}
            />
        );
    }
}
